// src/context/HeaderContext.tsx
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStores } from '@/stores';
import { HeaderContextType, NotificationItem } from '@/types/header';

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

interface HeaderProviderProps {
    children: React.ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
    const [activeMenu, setActiveMenu] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    
    const { pathname } = useLocation();
    const { educationStore } = useStores();

    // Determine active menu based on pathname
    const determineActiveMenu = useCallback((path: string) => {
        if (path === '/' || path.startsWith('/vietrro')) return 'home';
        if (path.startsWith('/dashboard')) return 'wall';
        if (path.startsWith('/documents')) return 'documents';
        if (path.startsWith('/education')) return 'education';
        if (path.startsWith('/user')) return 'profile';
        return '';
    }, []);

    // Update active menu when path changes
    useEffect(() => {
        setActiveMenu(determineActiveMenu(pathname));
        setIsMobileMenuOpen(false); // Close mobile menu on navigation
    }, [pathname, determineActiveMenu]);

    // Load notifications
    useEffect(() => {
        const loadNotifications = async () => {
            try {
                if (educationStore) {
                    await educationStore.fetchNotifications();
                    setNotifications(educationStore.notifications.slice(0, 10));
                    setUnreadCount(educationStore.unreadNotificationCount);
                } else {
                    // Fallback mock notifications
                    const mockNotifications: NotificationItem[] = [
                        {
                            id: 'notif1',
                            title: 'Thông báo mới',
                            content: 'Bạn có một thông báo mới từ hệ thống',
                            read: false,
                            createdAt: new Date().toISOString(),
                            type: 'info'
                        },
                        {
                            id: 'notif2',
                            title: 'Tài liệu đã được duyệt',
                            content: 'Tài liệu "Hướng dẫn sử dụng" đã được phê duyệt',
                            read: true,
                            createdAt: new Date(Date.now() - 3600000).toISOString(),
                            type: 'success'
                        }
                    ];
                    setNotifications(mockNotifications);
                    setUnreadCount(mockNotifications.filter(n => !n.read).length);
                }
            } catch (error) {
                console.error('Error loading notifications:', error);
            }
        };

        loadNotifications();
    }, [educationStore]);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(prev => !prev);
    }, []);

    const markNotificationAsRead = useCallback(async (id: string) => {
        try {
            if (educationStore) {
                await educationStore.markNotificationAsRead(id);
            } else {
                setNotifications(prev =>
                    prev.map(n => n.id === id ? { ...n, read: true } : n)
                );
            }
            setUnreadCount(prev => Math.max(prev - 1, 0));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, [educationStore]);

    const markAllNotificationsAsRead = useCallback(async () => {
        try {
            if (educationStore) {
                await educationStore.markAllNotificationsAsRead();
            } else {
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            }
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, [educationStore]);

    const contextValue: HeaderContextType = {
        activeMenu,
        setActiveMenu,
        isMobileMenuOpen,
        toggleMobileMenu,
        notifications,
        unreadCount,
        markNotificationAsRead,
        markAllNotificationsAsRead
    };

    return (
        <HeaderContext.Provider value={contextValue}>
            {children}
        </HeaderContext.Provider>
    );
};

// Custom hook to use header context
export const useHeader = (): HeaderContextType => {
    const context = useContext(HeaderContext);
    if (context === undefined) {
        throw new Error('useHeader must be used within a HeaderProvider');
    }
    return context;
};

// Additional hooks for specific header functionality
export const useHeaderNotifications = () => {
    const { notifications, unreadCount, markNotificationAsRead, markAllNotificationsAsRead } = useHeader();
    return {
        notifications,
        unreadCount,
        markAsRead: markNotificationAsRead,
        markAllAsRead: markAllNotificationsAsRead
    };
};

export const useHeaderNavigation = () => {
    const { activeMenu, setActiveMenu, isMobileMenuOpen, toggleMobileMenu } = useHeader();
    return {
        activeMenu,
        setActiveMenu,
        isMobileMenuOpen,
        toggleMobileMenu
    };
};
